import moment from "moment"
import { Formik } from 'formik'
import JobForm from '../JobForm'
import { JobSchema } from '@mmg/validation'
import React, { useRef, useState } from 'react'
import { useToast } from '../../../Hooks/toast'
import { ErrorMessage } from '../../../Elements'
import { useHasRight } from '../../../Hooks/hasRight'
import { useNavigate, useParams } from 'react-router-dom'
import { DEFAULT_JOB_DETAIL_STATE } from '../../../utils/state'
import { cloneDeep, difference, isEqual, reduce } from 'lodash'
import { ApolloConsumer, gql, useMutation } from '@apollo/client'
import { CAN_SUPERVISE_OTHERS } from '../../../utils/enums'
import { useLocalStorage, useOnlineUtils } from '../../../Hooks/offline'
import { getTempLatLon, shouldGetTemporaryLatLon } from '../JobForm/helpers'
import { useOfflineMutation, useOfflineQuery } from '../../../Hooks/graphql_utils'
import { selectiveObjectOverwrite, traverseObjectAndCompare } from '../../../utils/helpers'
import {
    DEFAULT_LOOKUP_RESULTS,
    GET_BASE_JOB_QUERY,
    GET_JOB_DETAIL_QUERY,
    JOB_FRAGMENT,
    LOOKUP_QUERY
} from '../../../utils/queries'

const DELETE_MUTATION = gql`
    mutation DeleteJob($id: ID!) {
        deleteJob(id:$id) {
            id
        }
    }
`

const DELETE_SUB_JOB_MUTATION = gql`
    mutation DeleteJobDetail($id: ID!) {
        deleteJobDetail(id:$id) {
            id
        }
    }
`

const MUTATION = gql`
    mutation UpdateJob($id: ID!, $job: JobInput!) {
        updateJob(id: $id, job: $job) {
            ...JobFragment
        }
    }
    ${JOB_FRAGMENT}
`

const fixSubJob = (sj, isLoading, isSyncing) => {
    sj.pebble_details = sj.pebble_details || DEFAULT_JOB_DETAIL_STATE.pebble_details
    sj.gunite_details = sj.gunite_details || DEFAULT_JOB_DETAIL_STATE.gunite_details
    sj.boulder_details = sj.boulder_details || DEFAULT_JOB_DETAIL_STATE.boulder_details
    sj.warranty_details = sj.warranty_details || DEFAULT_JOB_DETAIL_STATE.warranty_details
    sj.presite = sj.presite || DEFAULT_JOB_DETAIL_STATE.presite
    sj.is_still_loading = isLoading
    sj.is_syncing = isSyncing
}

const fixJob = (data, existingDetailIDs) => {
    if (data.job) {
        data.job.loadingBaseJob = false
    }
    if (data.job.sub_jobs) {
        data.job.sub_jobs.forEach(sj => {
            const detailExists = existingDetailIDs.includes(sj.id)
            if (detailExists) {
                fixSubJob(sj, undefined, true)
            } else {
                fixSubJob(sj, true)
            }
        })
    }
}

export default () => (
    <ApolloConsumer>
        {(client) => <EditJobScreen client={client} />}
    </ApolloConsumer>
)

export const EditJobScreen = ({ client }) => {
    const { jobID, jobDetailID } = useParams()
    const navigate = useNavigate()
    const { showSuccess, showError } = useToast()
    const [updateJob] = useOfflineMutation(MUTATION)
    const [shouldRefreshDocuments, setShouldRefreshDocuments] = useState(false)
    const [deleteJob] = useMutation(DELETE_MUTATION)
    const [deleteSubJob] = useMutation(DELETE_SUB_JOB_MUTATION)
    const { setMutatedStorage, deleteLocalKey, localState, setLocalStorage } = useLocalStorage()
    const { isOnline } = useOnlineUtils()
    const isSupervisor = useHasRight(CAN_SUPERVISE_OTHERS)

    // Set initial state from cache, if present
    const DEFAULT_EMPTY_JOB = { sub_jobs: [] }
    const cachedJob = localState?.job?.find(job => job.id === jobID)
    const [initialValues, setInitialValues] = useState(cachedJob || DEFAULT_EMPTY_JOB)
    const formRef = useRef()
    const enableLoadingState = !cachedJob

    const cachedDetailIDs = cachedJob?.sub_jobs?.map(detail => detail.id) || []

    const smartJobStateMerge = (job) => {
        const formCurrentRefClone = cloneDeep(formRef?.current?.values)
        const jobClone = cloneDeep(job)

        if (isEqual(DEFAULT_EMPTY_JOB, initialValues)) {
            // No cached job - short circuit
            setInitialValues(jobClone)
            return
        }

        let userUpdatedJobFields = reduce(initialValues, (result, value, key) => {
            return isEqual(value, formCurrentRefClone[key]) ? result : result.concat(key)
        }, [])

        if (userUpdatedJobFields.includes('sub_jobs')) {
            userUpdatedJobFields = userUpdatedJobFields.filter(field => field !== 'sub_jobs')
        }

        userUpdatedJobFields.forEach(field => {
            jobClone[field] = formCurrentRefClone[field]
        })

        // Re-set current form values to syncing?
        formCurrentRefClone.sub_jobs.forEach(sj => {
            fixSubJob(sj, undefined, true)
        })

        const subJobIDsFromDB = jobClone.sub_jobs.map(sj => sj.id)
        const subJobIDsonForm = formCurrentRefClone.sub_jobs.map(sj => sj.id)

        // Only pulls IDs present in database data that are not present locally, NOT the inverse
        const subJobIDsNotPresentLocally = difference(subJobIDsFromDB, subJobIDsonForm)
        if (subJobIDsNotPresentLocally.length > 0) {
            const newSubJobIDObjects = subJobIDsNotPresentLocally.map(sj => { return { id: sj, is_still_loading: true } })
            jobClone.sub_jobs = formCurrentRefClone.sub_jobs.concat(newSubJobIDObjects)
            setInitialValues(jobClone)
        } else {
            jobClone.sub_jobs = formCurrentRefClone.sub_jobs
            setInitialValues(jobClone)
        }
    }

    const smartSubJobStateMerge = (subJob) => {
        let subJobClone = cloneDeep(subJob)
        const formCurrentRefSubJobClone = cloneDeep(formRef?.current?.values?.sub_jobs)
            ?.find(sj => sj.id === subJobClone.id)
        const initialValueSubJob = initialValues?.sub_jobs?.find(sj => sj.id === subJobClone.id)

        if (!initialValueSubJob) {
            fixSubJob(subJobClone, false, false)
            // No cached sub-jobs - short circuit
            setInitialValues(state => {
                return {
                    ...state,
                    sub_jobs: state?.sub_jobs?.map(stateSubJob => stateSubJob.id === subJobClone.id ? subJobClone : stateSubJob)
                }
            })
            return subJobClone
        }

        let userUpdatedSubJobFields = traverseObjectAndCompare(formCurrentRefSubJobClone, initialValueSubJob)

        // Ignore loading state-controlled fields
        userUpdatedSubJobFields = userUpdatedSubJobFields.filter(field => !["is_syncing", "is_still_loading"].includes(field.key))
        fixSubJob(subJobClone, false, false)

        if (userUpdatedSubJobFields.length > 0) {
            // User did modify some fields, will prioritize those over DB-retrieved fields
            subJobClone = selectiveObjectOverwrite(subJobClone, userUpdatedSubJobFields)
        }

        setInitialValues(state => {
            return {
                ...state,
                sub_jobs: state?.sub_jobs?.map(stateSubJob => stateSubJob.id === subJobClone.id ? subJobClone : stateSubJob)
            }
        })
        return subJobClone
    }

    const markSubJobRetrievalError = (subJob, error) => {
        const subJobClone = cloneDeep(subJob)
        subJobClone.has_error = true
        subJobClone.error = error
        subJobClone.is_syncing = false
        subJobClone.is_still_loading = false

        setInitialValues(state => {
            return {
                ...state,
                sub_jobs: state?.sub_jobs?.map(stateSubJob => stateSubJob.id === subJobClone.id ? subJobClone : stateSubJob)
            }
        })
    }

    const startFetchingSubjobs = async (job) => {
        const fetchSubJobPromises = job?.sub_jobs?.map(sj =>
            client.query({
                query: GET_JOB_DETAIL_QUERY,
                variables: { job_detail_id: sj.id },
            })
                .then(res => smartSubJobStateMerge(res?.data?.job_detail))
                .catch(err => markSubJobRetrievalError(sj, err))
        )
        return await Promise.all(fetchSubJobPromises)
    }

    const markSubJobsAsNotLoadingAndSyncing = () => {
        setInitialValues(state => ({
            ...state,
            sub_jobs: state?.sub_jobs?.map(stateSubJob => ({
                ...stateSubJob,
                is_still_loading: false,
                is_syncing: false
            }))
        }))
    }

    const supervisorCacheJob = (job, subJobs) => {
        if (!isSupervisor || !localState?.job) {
            return
        }

        const existingCachedJobs = localState?.job
        // Check for job already present in cache
        let jobToCache, cachedJobExists
        const cachedJob = existingCachedJobs?.find(x => x.id === job.id)
        if (cachedJob) {
            if (cachedJob.expiresAt) { // Refresh expiration if exists
                cachedJob.expiresAt = moment().add(2, 'days').unix() * 1000
                jobToCache = cachedJob
                cachedJobExists = true
            } else {
                // No re-caching of jobs supervisors are already assigned to
                return
            }
        } else {
            jobToCache = job
            jobToCache.expiresAt = moment().add(2, 'days').unix() * 1000
        }

        // If not present, add job to cache
        subJobs.forEach(sj => {
            sj.is_still_loading = false
            sj.is_syncing = false
        })
        jobToCache.sub_jobs = subJobs

        const localStateClone = cloneDeep(localState)
        if (cachedJobExists) {
            // Remove old cached job if exists
            localStateClone.job = localStateClone.job.filter(x => x.id !== job.id)
        }
        localStateClone.job.push(jobToCache)
        setLocalStorage(localStateClone)
    }

    const { loading: loadingBaseJob, error, useCached: useCachedJob } = useOfflineQuery(GET_BASE_JOB_QUERY, {
        variables: {
            id: jobID
        },
        onCompleted: (data) => {
            if (data?.job) {
                fixJob(data, cachedDetailIDs)
                smartJobStateMerge(data.job)

                if (isOnline) {
                    startFetchingSubjobs(data.job)
                        .then(subJobs => supervisorCacheJob(data.job, subJobs))
                } else {
                    markSubJobsAsNotLoadingAndSyncing()
                }
            }
        }
    })

    const { error: lookupError, data: lookupData, useCached: useCachedLookup, loading: lookupLoading } = useOfflineQuery(LOOKUP_QUERY, {
        fetchPolicy: 'no-cache'
    })

    if (error && !useCachedJob) {
        return <ErrorMessage />
    }
    if (lookupError && !useCachedLookup) {
        return <ErrorMessage message='Lookup error' />
    }

    const onDelete = async () => {
        await deleteJob({
            variables: {
                id: jobID
            }
        }).then(() => {
            navigate('/job')
        })
    }

    const onSubJobDelete = async (props, { setValues }) => {
        props.job.sub_jobs.splice(props.job_detail_index, 1)
        const jobMinusDeletedDetail = { ...props.job, sub_jobs: [...props.job.sub_jobs] }
        // check for id; delete from database if id present, else remove from array
        if (props.job_detail_id && props.job_detail_id > 0) {
            await deleteSubJob({
                variables: {
                    id: props.job_detail_id
                }
            })
                .then(() => {
                    setValues(jobMinusDeletedDetail)
                    setInitialValues(jobMinusDeletedDetail)
                })
                .catch(() => showError('Error deleting sub job'))
        }
        else {
            setValues(jobMinusDeletedDetail)
            setInitialValues(jobMinusDeletedDetail)
        }
    }

    const onSubmit = async (props, { setValues }) => {
        let submitValues = { ...props }

        //remove jobDetails for which the user has not selected a category.
        submitValues.sub_jobs = submitValues.sub_jobs?.filter(sj => sj.job_category?.id)

        submitValues.sub_jobs.forEach(sj => {
            // what should be a temporary workaround for ID not being returned correctly for pebble_details
            if (sj.pebble_details) sj.pebble_details.id = !isNaN(sj.pebble_details.id) ? Number(sj.pebble_details.id) : sj.pebble_details.id
            // end workaround
        })
        let model = JobSchema.cast(submitValues, { stripUnknown: true })

        //Get temporary lat and lon if applicable
        if (shouldGetTemporaryLatLon(model)) {
            model = await getTempLatLon(model)
        }

        setMutatedStorage(`job:${jobID}`, { ...model, id: jobID })

        await updateJob({
            variables: {
                id: jobID,
                job: {
                    ...model,
                    lat: props?.isValidAddress ? model.lat : null,
                    lon: props?.isValidAddress ? model.lon : null
                }
            }
        })
            .then(({ data }) => {
                deleteLocalKey(`job:${jobID}`)
                setUpdatedCachedValue(data)
                setValues(data.updateJob)
                setInitialValues(data.updateJob)
                setShouldRefreshDocuments(!!submitValues.estimate_to_merge)
                showSuccess('Success!')
            })
            .catch(() => showError('Error, please try again'))
    }

    const onReset = () => navigate('/job')

    const setUpdatedCachedValue = (data) => {
        if(data?.updateJob){
            let jobs = localState['job']
            let cachedJobIndex = jobs?.findIndex(j => j.id === data.updateJob.id)
    
            if (cachedJobIndex >= 0) {
                jobs[cachedJobIndex] = data.updateJob
                setMutatedStorage(`job`, jobs)
            }
        }
    }

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues || []}
            onSubmit={onSubmit}
            render={props => {
                return <JobForm
                    {...props}

                    loadingBaseJob={loadingBaseJob && enableLoadingState}
                    lookupLoading={lookupLoading}
                    lookupOptions={cloneDeep(lookupData) || DEFAULT_LOOKUP_RESULTS}
                    customers={lookupData?.customers || []}
                    homeowners={lookupData?.homeowners || []}
                    supervisors={lookupData?.customer_contacts || []}
                    documentTags={lookupData?.lookupDocumentTag || []}

                    defaultJobDetailId={jobDetailID}
                    hardRefreshDocuments={shouldRefreshDocuments}
                    setHardRefreshDocuments={setShouldRefreshDocuments}
                    onCancel={onReset}
                    onDelete={onDelete}
                    onSubJobDelete={onSubJobDelete}
                />
            }}
            validationSchema={JobSchema}
            innerRef={formRef}
        />
    )
}
